import { FC } from 'react';

import { Controller, useFormContext } from 'react-hook-form-mui';
import { ArrowIcon } from 'assets';
import { Button, Input, PhoneInput } from 'view/components';
import * as S from 'view/modals/OnboardingWizard/styled';
import { LocationAutocomplete } from 'view/pages/profile/components';

interface PersonalInfoStepProps {
  onNext: () => void;
  onBack: () => void;
  isSaving?: boolean;
}

const JobSeekerPersonalInfoStep: FC<PersonalInfoStepProps> = ({
  onNext,
  onBack,
  isSaving = false,
}) => {
  const {
    watch,
    setValue,
    control,
    formState: { errors },
  } = useFormContext();

  const firstNameValue = watch('firstName') ?? '';
  const lastNameValue = watch('lastName') ?? '';
  const contactEmailValue = watch('contactEmail') ?? '';
  const currentLocationValue = watch('currentLocation') ?? null;

  // Phone is optional, so not included in validation
  const isValid =
    firstNameValue &&
    lastNameValue &&
    contactEmailValue &&
    currentLocationValue &&
    !errors?.firstName &&
    !errors?.lastName &&
    !errors?.contactEmail;

  return (
    <>
      <S.WizardContent>
        <S.StepHeader>
          <S.StepTitle>Personal Information</S.StepTitle>
          <S.StepSubtitle
            style={{
              maxWidth: 374,
            }}
          >
            Your profile highlights your skills, experience, and potential to match you with the
            right opportunities
          </S.StepSubtitle>
        </S.StepHeader>
        <S.StepFormContainerCompact style={{ gap: 16 }}>
          <S.InputWithCheckboxWrapper
            style={{
              marginBottom: 0,
            }}
          >
            <Input
              label="First Name"
              name="firstName"
              type="text"
              placeholder="Enter your first name"
              marginbottom={0}
            />
          </S.InputWithCheckboxWrapper>
          <S.InputWithCheckboxWrapper>
            <Input
              label="Last Name"
              name="lastName"
              type="text"
              placeholder="Enter your last name"
              marginbottom={0}
            />
          </S.InputWithCheckboxWrapper>
          <S.InputWithCheckboxWrapper>
            <Input
              label="Email Address"
              name="contactEmail"
              type="email"
              placeholder="e.g. youremail@email.com"
              marginbottom={8}
            />
            <S.CheckboxWithText>
              <Controller
                name="showEmail"
                control={control}
                render={({ field }) => {
                  return (
                    <S.CustomCheckbox
                      checked={field.value || false}
                      onChange={(e) => {
                        return field.onChange(e.target.checked);
                      }}
                    />
                  );
                }}
              />
              <p>Use this email for recruiters communication</p>
            </S.CheckboxWithText>
          </S.InputWithCheckboxWrapper>
          <S.InputWithCheckboxWrapper>
            <Controller
              name="contactPhone"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <PhoneInput
                    label="Phone (optional)"
                    value={field.value}
                    onChange={field.onChange}
                    error={fieldState.error?.message}
                    marginbottom={8}
                  />
                );
              }}
            />
            <S.CheckboxWithText>
              <Controller
                name="showPhone"
                control={control}
                render={({ field }) => {
                  return (
                    <S.CustomCheckbox
                      checked={field.value || false}
                      onChange={(e) => {
                        return field.onChange(e.target.checked);
                      }}
                    />
                  );
                }}
              />
              <p>Use this phone for recruiters communication</p>
            </S.CheckboxWithText>
          </S.InputWithCheckboxWrapper>
          <LocationAutocomplete
            setValue={setValue}
            currentLocationValue={currentLocationValue}
            label="Location"
          />
        </S.StepFormContainerCompact>
        <S.WizardFooter>
          <Button variant="outlined" text="Back" onClick={onBack} disabled={isSaving} />
          <Button
            variant="contained"
            text={isSaving ? 'Saving...' : 'Next'}
            onClick={onNext}
            disabled={!isValid || isSaving}
            endIcon={<ArrowIcon rotate="right" color="#FFFFFF" />}
          />
        </S.WizardFooter>
      </S.WizardContent>
    </>
  );
};

export default JobSeekerPersonalInfoStep;
